import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import type { Product } from '@/types/product'
import type { DesignCase } from '@/types/case'
import type { FavoriteItem, FavoriteType } from '@/types/favorite'
import { favoriteApi } from '@/api/favorite'
import { useUserStore } from './user'

export const useFavoriteStore = defineStore('favorite', () => {
  // 状态
  const favoriteItems = ref<FavoriteItem[]>([])
  const loading = ref(false)

  // 计算属性
  const totalCount = computed(() => favoriteItems.value.length)
  const isEmpty = computed(() => favoriteItems.value.length === 0)
  const productCount = computed(() =>
    favoriteItems.value.filter(item => item.itemType === 1).length
  )
  const caseCount = computed(() =>
    favoriteItems.value.filter(item => item.itemType === 2).length
  )

  // 方法

  /**
   * 从后端加载用户收藏列表
   */
  const loadFavorites = async (itemType?: FavoriteType) => {
    const userStore = useUserStore()
    if (!userStore.userInfo?.id) {
      favoriteItems.value = []
      return
    }

    try {
      loading.value = true
      const response = await favoriteApi.getUserFavorites(userStore.userInfo.id, itemType)

      // 转换后端返回的数据,将 designCase 字段映射为 case
      const items = response.data.data || []
      favoriteItems.value = items.map((item: any) => ({
        ...item,
        case: item.designCase,  // 将 designCase 映射为 case
        designCase: undefined   // 移除 designCase 字段
      }))

      console.log('✅ Favorites loaded:', favoriteItems.value)
    } catch (error: any) {
      console.error('❌ 加载收藏列表失败:', error)
      ElMessage.error('加载收藏列表失败')
      favoriteItems.value = []
    } finally {
      loading.value = false
    }
  }

  /**
   * 添加收藏（产品）
   */
  const addFavorite = async (product: Product) => {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      ElMessage.warning('请先登录')
      return false
    }

    try {
      loading.value = true

      // 检查是否已收藏
      if (isFavorite(product.id, 1)) {
        ElMessage.warning('该产品已在收藏列表中')
        return false
      }

      // 调用后端API添加收藏
      const response = await favoriteApi.addFavorite(userStore.userInfo.id, {
        itemId: product.id,
        itemType: 1 // 产品类型
      })

      // 获取返回的收藏数据(后端会自动填充product信息)
      const favoriteData = response.data.data

      // 将新收藏添加到本地状态,优先使用后端返回的product数据
      const newFavorite: FavoriteItem = {
        id: favoriteData.id,
        userId: favoriteData.userId,
        itemId: favoriteData.itemId,
        itemType: favoriteData.itemType,
        createdTime: favoriteData.createdTime,
        product: favoriteData.product || product // 使用后端返回的product,fallback到传入的product
      }

      favoriteItems.value.unshift(newFavorite)
      console.log('✅ Product favorite added successfully:', newFavorite)
      ElMessage.success('收藏成功')
      return true
    } catch (error: any) {
      console.error('添加收藏失败:', error)
      ElMessage.error(error.message || '添加收藏失败')
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 添加案例收藏
   */
  const addCaseFavorite = async (designCase: DesignCase) => {
    console.log('🏪 addCaseFavorite called')
    console.log('🏪 Case ID:', designCase.id)
    console.log('🏪 Case data:', designCase)

    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      console.log('❌ User not logged in')
      ElMessage.warning('请先登录')
      return false
    }

    console.log('🏪 User ID:', userStore.userInfo.id)
    console.log('🏪 Current favoriteItems:', favoriteItems.value)

    try {
      loading.value = true

      // 检查是否已收藏
      const alreadyFavorited = isFavorite(designCase.id, 2)
      console.log('🏪 Already favorited check:', alreadyFavorited)

      if (alreadyFavorited) {
        console.log('⚠️ Case already in favorites, returning false')
        ElMessage.warning('该案例已在收藏列表中')
        return false
      }

      // 调用后端API添加收藏
      console.log('🏪 Calling API to add favorite...')
      const response = await favoriteApi.addFavorite(userStore.userInfo.id, {
        itemId: designCase.id,
        itemType: 2 // 案例类型
      })
      console.log('🏪 API response:', response)

      // 获取返回的收藏数据(后端会自动填充case信息)
      const favoriteData = response.data.data
      console.log('🏪 Favorite data:', favoriteData)

      // 将新收藏添加到本地状态,优先使用后端返回的case数据
      // 注意:后端返回的字段是 designCase,需要映射为 case
      const newFavorite: FavoriteItem = {
        id: favoriteData.id,
        userId: favoriteData.userId,
        itemId: favoriteData.itemId,
        itemType: favoriteData.itemType,
        createdTime: favoriteData.createdTime,
        case: favoriteData.designCase || designCase // 将 designCase 映射为 case
      }

      favoriteItems.value.unshift(newFavorite)
      console.log('✅ Case favorite added successfully:', newFavorite)
      ElMessage.success('收藏成功')
      return true
    } catch (error: any) {
      console.error('❌ 添加收藏失败:', error)
      ElMessage.error(error.message || '添加收藏失败')
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 取消收藏（根据项目ID和类型）
   */
  const removeFavorite = async (itemId: number, itemType: FavoriteType = 1) => {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      ElMessage.warning('请先登录')
      return false
    }

    try {
      loading.value = true

      // 调用后端API取消收藏（使用 itemId 和 itemType 而不是 favoriteId）
      await favoriteApi.removeFavoriteByItem(userStore.userInfo.id, itemId, itemType)

      // 从本地状态移除
      const index = favoriteItems.value.findIndex(
        item => item.itemId === itemId && item.itemType === itemType
      )
      if (index > -1) {
        favoriteItems.value.splice(index, 1)
      }

      console.log('✅ Favorite removed successfully')
      ElMessage.success('已取消收藏')
      return true
    } catch (error: any) {
      console.error('❌ 取消收藏失败:', error)
      ElMessage.error(error.message || '取消收藏失败')
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 切换收藏状态（产品）
   */
  const toggleFavorite = async (product: Product) => {
    if (isFavorite(product.id, 1)) {
      return await removeFavorite(product.id, 1)
    } else {
      return await addFavorite(product)
    }
  }

  /**
   * 切换案例收藏状态
   */
  const toggleCaseFavorite = async (designCase: DesignCase) => {
    if (isFavorite(designCase.id, 2)) {
      return await removeFavorite(designCase.id, 2)
    } else {
      return await addCaseFavorite(designCase)
    }
  }

  /**
   * 清空用户收藏
   */
  const clearFavorites = async (itemType?: FavoriteType) => {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      return false
    }

    try {
      loading.value = true

      // 调用后端API清空收藏
      await favoriteApi.clearFavorites(userStore.userInfo.id, itemType)

      // 清空本地状态
      if (itemType) {
        favoriteItems.value = favoriteItems.value.filter(
          item => item.itemType !== itemType
        )
      } else {
        favoriteItems.value = []
      }

      return true
    } catch (error: any) {
      console.error('清空收藏失败:', error)
      ElMessage.error(error.message || '清空收藏失败')
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 检查是否已收藏
   */
  const isFavorite = (itemId: number, itemType: FavoriteType = 1) => {
    return favoriteItems.value.some(
      item => item.itemId === itemId && item.itemType === itemType
    )
  }

  /**
   * 检查是否已收藏（从后端查询，用于确保准确性）
   */
  const checkFavorite = async (itemId: number, itemType: FavoriteType = 1) => {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      return false
    }

    try {
      const response = await favoriteApi.isFavorited(
        userStore.userInfo.id,
        itemId,
        itemType
      )
      return response.data.data
    } catch (error) {
      console.error('检查收藏状态失败:', error)
      return false
    }
  }

  /**
   * 获取收藏的产品
   */
  const getFavoriteProduct = (productId: number) => {
    const favorite = favoriteItems.value.find(
      item => item.itemId === productId && item.itemType === 1
    )
    return favorite?.product
  }

  /**
   * 获取收藏的案例
   */
  const getFavoriteCase = (caseId: number) => {
    const favorite = favoriteItems.value.find(
      item => item.itemId === caseId && item.itemType === 2
    )
    return favorite?.case
  }

  /**
   * 批量添加收藏
   */
  const addMultipleFavorites = async (products: Product[]) => {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      ElMessage.warning('请先登录')
      return 0
    }

    try {
      loading.value = true
      const items = products.map(product => ({
        itemId: product.id,
        itemType: 1 as FavoriteType
      }))

      const response = await favoriteApi.addMultipleFavorites(
        userStore.userInfo.id,
        items
      )

      // 重新加载收藏列表
      await loadFavorites()

      const addedCount = response.data.data?.length || 0
      ElMessage.success(`成功添加 ${addedCount} 件商品到收藏`)
      return addedCount
    } catch (error: any) {
      console.error('批量添加收藏失败:', error)
      ElMessage.error(error.message || '批量添加收藏失败')
      return 0
    } finally {
      loading.value = false
    }
  }

  /**
   * 批量移除收藏
   */
  const removeMultipleFavorites = async (favoriteIds: number[]) => {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      return 0
    }

    try {
      loading.value = true

      await favoriteApi.removeMultipleFavorites(
        userStore.userInfo.id,
        favoriteIds
      )

      // 从本地状态移除
      favoriteItems.value = favoriteItems.value.filter(
        item => !favoriteIds.includes(item.id)
      )

      ElMessage.success(`已移除 ${favoriteIds.length} 件收藏`)
      return favoriteIds.length
    } catch (error: any) {
      console.error('批量移除收藏失败:', error)
      ElMessage.error(error.message || '批量移除收藏失败')
      return 0
    } finally {
      loading.value = false
    }
  }

  /**
   * 获取收藏摘要信息
   */
  const getFavoriteSummary = () => {
    return {
      totalCount: totalCount.value,
      productCount: productCount.value,
      caseCount: caseCount.value,
      isEmpty: isEmpty.value,
      latestItems: favoriteItems.value.slice(0, 5)
    }
  }

  /**
   * 获取收藏统计
   */
  const getFavoriteStatistics = async () => {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn || !userStore.userInfo?.id) {
      return {
        totalCount: 0,
        productCount: 0,
        caseCount: 0
      }
    }

    try {
      const response = await favoriteApi.getFavoriteStatistics(userStore.userInfo.id)
      return response.data.data
    } catch (error) {
      console.error('获取收藏统计失败:', error)
      return {
        totalCount: 0,
        productCount: 0,
        caseCount: 0
      }
    }
  }

  /**
   * 按分类筛选收藏产品
   */
  const getFavoritesByCategory = (categoryId?: number) => {
    const products = favoriteItems.value
      .filter(item => item.itemType === 1 && item.product)
      .map(item => item.product!)

    if (!categoryId) {
      return products
    }
    return products.filter(item => item.categoryId === categoryId)
  }

  /**
   * 搜索收藏
   */
  const searchFavorites = (keyword: string) => {
    if (!keyword.trim()) {
      return favoriteItems.value
    }

    const lowerKeyword = keyword.toLowerCase()
    return favoriteItems.value.filter(item => {
      if (item.itemType === 1 && item.product) {
        return (
          item.product.productName?.toLowerCase().includes(lowerKeyword) ||
          item.product.productTitle?.toLowerCase().includes(lowerKeyword) ||
          item.product.productTags?.toLowerCase().includes(lowerKeyword)
        )
      } else if (item.itemType === 2 && item.case) {
        return (
          item.case.caseName?.toLowerCase().includes(lowerKeyword) ||
          item.case.description?.toLowerCase().includes(lowerKeyword)
        )
      }
      return false
    })
  }

  return {
    // 状态
    favoriteItems,
    loading,

    // 计算属性
    totalCount,
    isEmpty,
    productCount,
    caseCount,

    // 方法
    loadFavorites,
    addFavorite,
    addCaseFavorite,
    removeFavorite,
    toggleFavorite,
    toggleCaseFavorite,
    clearFavorites,
    isFavorite,
    checkFavorite,
    getFavoriteProduct,
    getFavoriteCase,
    addMultipleFavorites,
    removeMultipleFavorites,
    getFavoriteSummary,
    getFavoriteStatistics,
    getFavoritesByCategory,
    searchFavorites
  }
}, {
  // 配置持久化
  persist: {
    key: 'favorite-store',
    storage: localStorage,
    paths: ['favoriteItems'] // 只持久化 favoriteItems
  }
})
